import { Col, Form, Row, Select } from "antd";
import { Icon } from "@iconify/react";

import { useUserDirectory } from "../../../Hooks/useUserDirectory";
import { AdminEntry } from "../Constants";
import styles from "../ApprovalManagement.module.scss";

interface Props {
  admins: AdminEntry[];
  setAdmins: (admins: AdminEntry[]) => void;
}

export const blankAdmin = (seed: number): AdminEntry => ({
  key: `admin-${seed}`,
  name: "",
  email: "",
});

/**
 * Admin rows that repeat an earlier one, keyed by row.
 *
 * The same person could be added several times over, which is meaningless as
 * configuration and made deleting look broken: with three identical rows,
 * removing one left two that looked exactly the same. Only the later
 * occurrence is flagged, so the first stays usable.
 *
 * Exported because the parent forms gate Submit on it.
 */
export const findDuplicateAdmins = (admins: AdminEntry[]): Map<string, string> => {
  const seen = new Set<string>();
  const flagged = new Map<string, string>();

  admins.forEach((admin) => {
    if (!admin.name && !admin.email) return;
    const identity = `${admin.name}|${admin.email}`.trim().toLowerCase();
    if (seen.has(identity)) flagged.set(admin.key, "This user is already added above.");
    else seen.add(identity);
  });

  return flagged;
};

/**
 * Repeatable Name+Email admin rows, shared by the Subsidiary/Division and
 * Cost Center forms.
 *
 * The two selects are linked — picking a name fills the matching email and
 * vice versa — because both read the same admin directory, and letting them
 * drift would produce an admin matching no real user.
 */
export const AdminRows = ({ admins, setAdmins }: Props) => {
  const duplicates = findDuplicateAdmins(admins);
  /* Admin directory backing both selects — the admin users the API knows
     about, rather than a hardcoded list. */
  const { users: adminDirectory, byName, byEmail } = useUserDirectory("admin");

  const updateAdmin = (key: string, patch: Partial<AdminEntry>) =>
    setAdmins(admins.map((admin) => (admin.key === key ? { ...admin, ...patch } : admin)));

  return (
    <>
      {admins.map((admin) => (
        <Row
          gutter={24}
          key={admin.key}
          align="top"
        >
          <Col
            xs={24}
            sm={10}
            lg={8}
          >
            <Form.Item required label="Name">
              <Select
                showSearch
                optionFilterProp="children"
                value={admin.name || undefined}
                getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                onChange={(value: string) => {
                  const match = adminDirectory.find((entry) => entry.name === value);
                  updateAdmin(admin.key, { name: value, email: match?.email ?? admin.email });
                }}
              >
                {byName.map((entry, index) => (
                  <Select.Option
                    value={entry.name}
                    key={`${index}`}
                  >
                    {entry.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col
            xs={24}
            sm={10}
            lg={8}
          >
            <Form.Item required
              label="Email"
              validateStatus={duplicates.has(admin.key) ? "error" : undefined}
              help={duplicates.get(admin.key)}
            >
              <Select
                showSearch
                optionFilterProp="children"
                value={admin.email || undefined}
                getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                onChange={(value: string) => {
                  const match = adminDirectory.find((entry) => entry.email === value);
                  updateAdmin(admin.key, { email: value, name: match?.name ?? admin.name });
                }}
              >
                {byEmail.map((entry, index) => (
                  <Select.Option
                    value={entry.email}
                    key={`${index}`}
                  >
                    {entry.email}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col>
            <Icon
              icon="fluent:delete-48-regular"
              className={styles.adminRowDelete}
              onClick={() =>
                // Keep at least one row so the section never renders empty.
                setAdmins(
                  admins.length === 1
                    ? [blankAdmin(0)]
                    : admins.filter((entry) => entry.key !== admin.key)
                )
              }
            />
          </Col>
        </Row>
      ))}

      <Row justify="end">
        <Col>
          <span
            className={styles.addAnother}
            onClick={() => setAdmins([...admins, blankAdmin(admins.length + 1)])}
          >
            <Icon
              icon="akar-icons:plus"
              inline
            />
            Add Another
          </span>
        </Col>
      </Row>
    </>
  );
};
